import { isSuccess } from '@devexperts/remote-data-ts'
import { Button, cn } from '@spiaggeit/spit-ui'
import { memo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useMediaQuery, useOnClickOutside } from 'usehooks-ts'
import { Cart } from '@/assets/icons/Cart'
import { ExclamationMark } from '@/assets/icons/ExclamationMark'

import { BackButton } from '@/components/BackButton/index'
import { useAppDispatch, useAppSelector } from '@/hooks/store'
import { useNextPathAfterChoosingSpot } from '@/hooks/useNextPathAfterChoosingSpot'
import { isKioskSelector } from '@/store/appSlice'
import { cartSlice } from '@/store/cartSlice'
import { cartReservationErrorsSelector } from '@/store/cartSlice/selectors/cartReservationErrorsSelector'
import { cartReservationsLenghtSelector } from '@/store/cartSlice/selectors/cartReservationsLenghSelector'
import { extraSlice } from '@/store/extraSlice'
import { toastSlice } from '@/store/toastSlice'
import { BREAKPOINTS } from '@/utils/breakpoints'
import { priceFormatter } from '@/utils/price'
import { getReservationValidationToastMessage } from '@/utils/reservationValidationMessages'

import { ServiceCostDialog } from '../../ServiceCostDialog'
import { CartPopupContent } from '../CartPopupContent'

interface CartOverviewProps {
  nextPath?: string
}

export const CartOverview = memo(function CartOverview(
  props: CartOverviewProps
) {
  const popoverRef = useRef<HTMLDivElement | null>(null)
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const isDesktop = useMediaQuery(`(min-width: ${BREAKPOINTS.lg}px)`)
  const cartReservationsLength = useAppSelector(cartReservationsLenghtSelector)
  const reservationErrors = useAppSelector(cartReservationErrorsSelector)
  const isKiosk = useAppSelector(isKioskSelector)
  const cartTotal = useAppSelector(cartSlice.selectors.total)
  const cartPopoverIsOpen = useAppSelector(
    cartSlice.selectors.cartPopoverIsOpen
  )
  const isKioskMode = useAppSelector(isKioskSelector)
  const isWeatherInsuranceAddedToCart = useAppSelector(
    cartSlice.selectors.isWeatherInsuranceAddedToCart
  )
  const weatherInsurancePricePreview = useAppSelector(
    extraSlice.selectors.weatherInsurancePricePreview
  )
  const nextPath = useNextPathAfterChoosingSpot(props.nextPath)

  const handleProceed = () => {
    if (cartReservationsLength < 1 || !nextPath) {
      return
    }

    if (reservationErrors.length > 0) {
      dispatch(
        toastSlice.actions.show({
          message: getReservationValidationToastMessage(t, reservationErrors),
        })
      )
      return
    }

    navigate({
      pathname: nextPath,
      search: searchParams.toString(),
    })
  }

  const formattedTotal = priceFormatter.format(
    isSuccess(weatherInsurancePricePreview) && isWeatherInsuranceAddedToCart
      ? weatherInsurancePricePreview.value + cartTotal
      : cartTotal
  )

  useOnClickOutside(popoverRef, () => {
    if (!isDesktop) {
      return
    }

    if (cartPopoverIsOpen) {
      dispatch(cartSlice.actions.setCartPopoverIsOpen(false))
    }
  })

  return (
    <div
      className={cn(
        'relative flex max-w-full basis-full items-center justify-between gap-4 lg:basis-auto',
        {
          'border-t p-4 lg:border-t-0 lg:p-0': cartPopoverIsOpen,
          'lg:ml-auto': !isKioskMode,
          'w-full flex-col items-start': isKioskMode,
        }
      )}
    >
      <div className="flex min-w-0 items-center gap-2">
        <div
          className={cn(
            `absolute top-0 hidden w-[400px] translate-x-[-50%] translate-y-[-100%] rounded-lg bg-white px-4 pt-4 drop-shadow-md 
    transition-opacity duration-300 after:absolute after:-bottom-0 after:left-1/2 after:h-0 after:w-0
    after:-translate-x-1/2 after:translate-y-[100%] after:border-l-[16px] after:border-r-[16px]
    after:border-t-[12px] after:border-l-transparent after:border-r-transparent after:border-t-white 
    after:content-[''] lg:block`,
            {
              'opacity-100': cartPopoverIsOpen,
              'pointer-events-none opacity-0': !cartPopoverIsOpen,
            }
          )}
          ref={popoverRef}
        >
          <CartPopupContent />
        </div>

        <button
          aria-label={t('common.open')}
          className={cn('relative size-8 min-w-0 shrink-0', {
            'cursor-default': cartReservationsLength < 1,
          })}
          onClick={() =>
            cartReservationsLength >= 1 &&
            dispatch(cartSlice.actions.setCartPopoverIsOpen(!cartPopoverIsOpen))
          }
          type="button"
        >
          <span className="cart-overview__badge absolute bottom-4 left-4 flex min-h-5 min-w-5 items-center justify-center rounded-circle bg-brand text-xs text-white">
            {cartReservationsLength}
          </span>

          <Cart />
        </button>

        <div className="flex flex-col">
          <span className="left-0 top-1 w-20 font-bold text-primary">
            {formattedTotal}
          </span>

          {!isKiosk ? (
            <ServiceCostDialog>
              <button aria-label={t('common.open')} type="button">
                <span className="inline-flex items-center gap-1 whitespace-nowrap text-left text-sm text-secondary underline">
                  {t('tickets.summary.includedFees')}
                  <span className="text-base text-primary">
                    <ExclamationMark className="size-4" />
                  </span>
                </span>
              </button>
            </ServiceCostDialog>
          ) : null}
        </div>
      </div>

      <div className={cn({ 'flex w-full gap-4': isKioskMode })}>
        {isKioskMode && <BackButton />}

        {nextPath ? (
          <Button
            className="flex-1"
            disabled={cartReservationsLength < 1}
            onClick={handleProceed}
          >
            {t('common.proceed')}
          </Button>
        ) : null}
      </div>
    </div>
  )
})
